import type { AuthOutcome } from '@/auth/auth-gate';
import { msalInstance } from '@/auth/msal-config';
import { markLoggedOut } from '@/auth/login-gate';

// EW-92: a no-access visitor is authenticated but not entitled, so a plain reload only re-checks
// the same account and lands here again. Signing out (clears the MSAL cache + ends the Keycloak
// session) is the only way to come back on the login form and try another account. This screen
// renders outside MsalProvider (main.tsx), so it must not use MSAL hooks — the instance is already
// initialized by resolveAuth() before we get here.
function signInAsDifferentUser(): void {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? undefined;
  markLoggedOut();
  void msalInstance.logoutRedirect({
    account,
    // Keycloak's RP-initiated logout rejects the call without an id_token_hint; Entra ignores it,
    // so passing it is safe on both paths (mirrors topbar.tsx).
    idTokenHint: account?.idToken,
    postLogoutRedirectUri: window.location.origin,
  });
}

export function AuthError({ outcome }: { outcome: AuthOutcome }): JSX.Element {
  const noAccess = outcome.kind === 'no-access';
  const correlationId = 'correlationId' in outcome ? outcome.correlationId : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-6">
        <h1 className="text-lg font-semibold text-heading">
          {noAccess ? 'Nincs hozzáférésed az EchoProxy felületéhez' : 'A belépés nem sikerült'}
        </h1>

        <p className="mt-3 text-sm text-muted-foreground">
          {noAccess
            ? 'A fiókod nincs hozzárendelve az EchoProxy alkalmazáshoz, ezért az azonosítás nem fejezhető be. A hozzáférést az üzemeltetés tudja megadni — jelezd nekik, melyik fiókkal próbálkoztál, vagy lépj be egy jogosult fiókkal.'
            : 'Az azonosítás közben hiba történt. Próbáld újra, és ha ismét elbukik, jelezd az üzemeltetésnek.'}
        </p>

        {correlationId && (
          <p className="mt-3 text-xs text-muted-foreground">
            Hibaazonosító: <span className="font-mono">{correlationId}</span>
          </p>
        )}

        {noAccess ? (
          <button type="button" className="btn-primary mt-5" onClick={signInAsDifferentUser}>
            Belépés másik fiókkal
          </button>
        ) : (
          <button type="button" className="btn-primary mt-5" onClick={() => window.location.reload()}>
            Újrapróbálás
          </button>
        )}
      </div>
    </div>
  );
}
